// Competizioni del team (Impostazioni). Il nome e' univoco per team e le
// partite lo referenziano per id: rinominare qui aggiorna le partite (trigger
// competitions_propagate), eliminare le lascia senza competizione. Il campo
// stagione non si usa piu': le stagioni appartengono alle partite.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import { Switch } from "@v2/components/ui/switch";
import { cn } from "@v2/lib/utils";
import { EmptyState, SectionLoading } from "../../components/state";
import { supabaseV2 } from "../../lib/supabase";

const COMPETITION_TYPES = ["league", "cup", "tournament", "friendly"] as const;
type CompetitionType = (typeof COMPETITION_TYPES)[number];

type Competition = { id: string; name: string; type: CompetitionType | null; is_active: boolean; matches: number };

export function CompetitionsManager({ teamId }: { teamId: string }) {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [type, setType] = useState<CompetitionType | "">("");

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["competitions", teamId] });
    queryClient.invalidateQueries({ queryKey: ["matches", teamId] });
  };

  const competitionsQuery = useQuery({
    queryKey: ["competitions", teamId, "manager"],
    queryFn: async () => {
      const [{ data, error }, { data: matches, error: mErr }] = await Promise.all([
        supabaseV2
          .from("competitions")
          .select("id, name, type, is_active")
          .eq("team_id", teamId)
          .order("is_active", { ascending: false })
          .order("name"),
        supabaseV2.from("matches").select("competition_id").eq("team_id", teamId).not("competition_id", "is", null),
      ]);
      if (error) throw error;
      if (mErr) throw mErr;
      const counts = new Map<string, number>();
      for (const m of matches || []) counts.set(m.competition_id!, (counts.get(m.competition_id!) || 0) + 1);
      return ((data || []) as Array<Omit<Competition, "matches">>).map((c) => ({ ...c, matches: counts.get(c.id) || 0 }));
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      if (!name.trim()) return;
      const { error } = await supabaseV2.from("competitions").insert({
        team_id: teamId,
        name: name.trim(),
        type: type || null,
        is_active: true,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setName("");
      setType("");
      invalidate();
    },
    onError: (e: any) => window.alert(e?.message || String(e)),
  });
  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Pick<Competition, "name" | "type" | "is_active">> }) => {
      const { error } = await supabaseV2.from("competitions").update(patch).eq("id", id).eq("team_id", teamId);
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (e: any) => window.alert(e?.message || String(e)),
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabaseV2.from("competitions").delete().eq("id", id).eq("team_id", teamId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const competitions = competitionsQuery.data || [];
  const typeLabel = (v: string | null) => (v ? t(`competitions.types.${v}`) : t("competitions.typeNone"));
  const selectClass = "h-8 rounded-2xl bg-background px-2 text-sm";

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t("competitions.title")}</CardTitle>
        <CardDescription>{t("competitions.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <form
          className="flex flex-wrap gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("competitions.namePh")} required className="h-8 text-sm flex-1 min-w-[140px]" />
          <select value={type} onChange={(e) => setType(e.target.value as CompetitionType | "")} className={cn(selectClass, "w-40")} aria-label={t("competitions.typePh")}>
            <option value="">{t("competitions.typeNone")}</option>
            {COMPETITION_TYPES.map((k) => (
              <option key={k} value={k}>{t(`competitions.types.${k}`)}</option>
            ))}
          </select>
          <Button type="submit" size="sm" className="h-8" disabled={create.isPending || !name.trim()}>
            <Plus className="h-3 w-3" /> {t("competitions.add")}
          </Button>
        </form>
        {competitionsQuery.isLoading && <SectionLoading />}
        {!competitionsQuery.isLoading && competitions.length === 0 && (
          <EmptyState text={t("competitions.empty")} />
        )}
        {competitions.length > 0 && (
          <div className="rounded-2xl bg-background overflow-hidden max-h-80 overflow-y-auto">
            {competitions.map((comp, idx) => (
              <div
                key={comp.id}
                className={cn("flex flex-wrap sm:flex-nowrap items-center gap-2 px-3 py-2 text-sm", idx > 0 && "border-t", !comp.is_active && "opacity-60")}
              >
                <div className="basis-full sm:basis-0 flex-1 min-w-0">
                  <p className="font-medium truncate">{comp.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("competitions.matches", { count: comp.matches })}
                  </p>
                </div>
                <select
                  value={comp.type || ""}
                  onChange={(e) => update.mutate({ id: comp.id, patch: { type: (e.target.value || null) as CompetitionType | null } })}
                  className={cn(selectClass, "h-7 text-xs w-32")}
                  aria-label={t("competitions.typePh")}
                >
                  <option value="">{t("competitions.typeNone")}</option>
                  {COMPETITION_TYPES.map((k) => (
                    <option key={k} value={k}>{typeLabel(k)}</option>
                  ))}
                </select>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  title={t("competitions.rename")}
                  onClick={() => {
                    const next = window.prompt(t("competitions.renamePrompt", { name: comp.name }), comp.name);
                    if (next && next.trim() && next.trim() !== comp.name) update.mutate({ id: comp.id, patch: { name: next.trim() } });
                  }}
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <Switch
                  checked={comp.is_active}
                  onCheckedChange={(v) => update.mutate({ id: comp.id, patch: { is_active: v } })}
                  title={comp.is_active ? t("competitions.deactivate") : t("competitions.activate")}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-rose-600 hover:bg-rose-50"
                  onClick={() => {
                    if (window.confirm(t("competitions.deleteConfirm", { name: comp.name }))) remove.mutate(comp.id);
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
        {competitions.some((c) => !c.is_active) && (
          <p className="text-xs text-muted-foreground">{t("competitions.inactiveHint")}</p>
        )}
      </CardContent>
    </Card>
  );
}
